import { useState } from 'react';
import { X, Trash2, Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  useCabinetTags,
  useCreateCabinetTag,
  useDeleteCabinetTag,
} from '../../hooks/queries/useCabinetLibrary';
import { useAuthStore } from '../../store/useAuthStore';

const PALETTE = ['#64748b', '#0891b2', '#0d9488', '#16a34a', '#ca8a04', '#dc2626', '#9333ea', '#db2777'];

interface Props {
  onClose: () => void;
}

export function CabinetTagsManagerModal({ onClose }: Props) {
  const { data: tags = [] } = useCabinetTags();
  const create = useCreateCabinetTag();
  const remove = useDeleteCabinetTag();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id ?? null);

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(PALETTE[0]!);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    await create.mutateAsync({ name, color, createdBy: collaboratorId });
    setName('');
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold">Gérer les tags</h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>

        <form onSubmit={handleCreate} className="px-5 py-3 border-b border-neutral-200 space-y-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nouveau tag"
            maxLength={50}
          />
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-5 h-5 rounded-full border-2 ${color === c ? 'border-neutral-900' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  aria-label={c}
                />
              ))}
            </div>
            <div className="ml-auto">
              <Button type="submit" disabled={!name.trim() || create.isPending} size="sm">
                <Plus className="w-3.5 h-3.5" /> Créer
              </Button>
            </div>
          </div>
        </form>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {tags.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">Aucun tag.</p>
          ) : (
            <ul className="space-y-1">
              {tags.map((t) => (
                <li key={t.id} className="flex items-center gap-2 py-1">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="flex-1 text-sm">{t.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Supprimer le tag « ${t.name} » ?`)) {
                        remove.mutate(t.id);
                      }
                    }}
                    className="p-1 text-neutral-400 hover:text-red-600"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-end px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button onClick={onClose}>Fermer</Button>
        </footer>
      </div>
    </div>
  );
}
